import axios, {AxiosError, AxiosRequestHeaders, AxiosResponse, InternalAxiosRequestConfig} from "axios";
import {API_MAIN_ROOT} from "../routing/LinkFactory";
import Cookies from 'js-cookie';
import {AUTH_TOKEN} from "../constants/Constants";
import {clearAllCookies, getRefreshToken, setAuthTokenCookie, setRefreshTokenCookie} from "../utils/Utils";
import AxiosAgent from "./AxiosAgent";
import api from "./api";
// Create Axios instance
const instance = axios.create({
    baseURL: API_MAIN_ROOT,
    headers: {
        "Content-Type": "application/json",
    },
});
const noTokenInterceptor = axios.create({
    baseURL: API_MAIN_ROOT,
    headers: {
        "Content-Type": "application/json",
    },
});

// Request interceptor
instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const token = Cookies.get(AUTH_TOKEN);
        if (token) {
            (config.headers as AxiosRequestHeaders)["Authorization"] = `Bearer ${token}`;
        }
        // Add Time-Zone header
        (config.headers as AxiosRequestHeaders)["Time-Zone"] = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return config;
    },
    (error: AxiosError) => {
        return Promise.reject(error);
    }
);
noTokenInterceptor.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        // No token handling, just return the config as is
        return config;
    },
    (error: AxiosError) => {
        return Promise.reject(error);
    }
);
// Track in-flight token refresh to prevent race conditions
let isRefreshing = false;
let refreshSubscribers: {resolve: (token: string) => void; reject: (error: unknown) => void}[] = [];

const subscribeTokenRefresh = (resolve: (token: string) => void, reject: (error: unknown) => void) => {
    refreshSubscribers.push({resolve, reject});
};

const onTokenRefreshed = (token: string) => {
    refreshSubscribers.forEach(({resolve}) => resolve(token));
    refreshSubscribers = [];
};

const onRefreshFailed = (error: unknown) => {
    refreshSubscribers.forEach(({reject}) => reject(error));
    refreshSubscribers = [];
};

// Response interceptor
instance.interceptors.response.use(
    (response: AxiosResponse) => {
        return response;
    },
    async (error: AxiosError) => {
        const originalConfig = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
        if (!originalConfig.url?.includes("/auth/authenticate") && error.response) {
            if (error.response.status === 401 && !originalConfig._retry) {
                originalConfig._retry = true;

                // If a refresh is already in progress, queue this request
                if (isRefreshing) {
                    return new Promise((resolve, reject) => {
                        subscribeTokenRefresh((newToken: string) => {
                            (originalConfig.headers as AxiosRequestHeaders)["Authorization"] = `Bearer ${newToken}`;
                            resolve(instance(originalConfig));
                        }, reject);
                    });
                }

                isRefreshing = true;

                try {
                    const rs = await AxiosAgent.requests.postNoToken(api.USER + "/token-by-refresh-token", {
                        stringParam: getRefreshToken(),
                    });
                    if (rs.response?.status !== 200) {
                        isRefreshing = false;
                        onRefreshFailed(error);
                        await clearAllCookies();
                        window.location.href = "/";
                        return Promise.reject(error);
                    }
                    const newAccessToken = rs?.data?.access_token;
                    await setAuthTokenCookie(newAccessToken);
                    await setRefreshTokenCookie(rs?.data?.refresh_token);
                    isRefreshing = false;
                    onTokenRefreshed(newAccessToken);
                    // Retry the original request with new access token
                    return instance(originalConfig);
                } catch (_error) {
                    isRefreshing = false;
                    onRefreshFailed(_error);
                    console.error("Refresh token failed!");
                    await setAuthTokenCookie("");
                    await setRefreshTokenCookie("");
                    await clearAllCookies();
                    // going back to login
                    window.location.href = "/";
                    return Promise.reject(_error);
                }
            }
        }

        return Promise.reject(error);
    }
);

export {instance, noTokenInterceptor};
